import React, { useMemo, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import TextButton from 'components/TextButton/TextButton';
import type { DpiaClarificationHistoryEntry } from 'config/api/dpias/dpias.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import { getReviewHistoryDisplayState, shouldTruncateComment, truncateComment } from '../adminDpiaReview.helpers';

type DpiaReviewHistoryListProps = {
  entries: DpiaClarificationHistoryEntry[];
};

const formatTimestamp = (value: string) => {
  if (!value) {
    return '—';
  }

  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch {
    return value;
  }
};

const DpiaReviewHistoryList: React.FC<DpiaReviewHistoryListProps> = ({ entries }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.admin_dpia_review.sections.clarification_history;
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [expandedEntryIds, setExpandedEntryIds] = useState<number[]>([]);

  const { visibleEntries, hiddenCount, hasHiddenEntries } = useMemo(
    () => getReviewHistoryDisplayState(entries, { showAll: showAllHistory }),
    [entries, showAllHistory],
  );

  const toggleExpanded = (entryId: number) => {
    setExpandedEntryIds(current => (current.includes(entryId) ? current.filter(id => id !== entryId) : [...current, entryId]));
  };

  const getRoleLabel = (entry: DpiaClarificationHistoryEntry) =>
    entry.authorRole === 'owner' ? t(messages.role_owner) : t(messages.role_administrator);

  return (
    <Box className={styles.reviewHistoryList}>
      {visibleEntries.map(entry => {
        const isExpanded = expandedEntryIds.includes(entry.id);
        const isTruncated = shouldTruncateComment(entry.comment);
        const displayText = isExpanded || !isTruncated ? entry.comment : truncateComment(entry.comment);

        return (
          <Box
            key={entry.id}
            className={clsx(
              styles.reviewHistoryEntry,
              entry.authorRole === 'administrator' ? styles.reviewHistoryEntryAdministrator : styles.reviewHistoryEntryOwner,
            )}
          >
            <Typography className={styles.reviewHistoryMeta} color='textSecondary' variant='caption'>
              {t(messages.comment_meta, {
                date: formatTimestamp(entry.createdAt),
                name: entry.authorName,
                role: getRoleLabel(entry),
              })}
            </Typography>
            <Typography
              aria-expanded={isExpanded}
              className={clsx(styles.reviewHistoryBody, styles.preLineValue, isTruncated && styles.reviewHistoryBodyClickable)}
              onClick={isTruncated ? () => toggleExpanded(entry.id) : undefined}
              onKeyDown={
                isTruncated
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleExpanded(entry.id);
                      }
                    }
                  : undefined
              }
              role={isTruncated ? 'button' : undefined}
              tabIndex={isTruncated ? 0 : undefined}
              variant='body2'
            >
              {displayText}
            </Typography>
          </Box>
        );
      })}

      {hasHiddenEntries && <TextButton onClick={() => setShowAllHistory(true)}>{t(messages.show_more, { count: hiddenCount })}</TextButton>}

      {showAllHistory && entries.length > 2 && <TextButton onClick={() => setShowAllHistory(false)}>{t(messages.show_less)}</TextButton>}
    </Box>
  );
};

export default DpiaReviewHistoryList;
